import {DestroyRef, inject, Injectable, Input, OnDestroy, OnInit} from "@angular/core";
import {User} from "../../../../user/user";
import {Subject} from "rxjs";
import {
  GlobalEventDispatcherService
} from "../../../../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {GlobalEvent} from "../../../../shared/services/global-event-dispatcher/global-event";
import * as fromStore from "../../../../store";
import {Store} from "@ngrx/store";
import {distinctUntilChanged, filter, first, map, switchMap, takeUntil} from "rxjs/operators";
import {combineLatest} from "rxjs";
import {HostingService} from "../../../../websites/website/hosting/hosting.service";
import {NotifierComponent} from "../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../shared/components/notifier/notification-type";
import {UserService} from "../../../../user/user.service";
import {Website} from "../../../../websites/website/website";
import {Product} from "../../../../websites/website/product";
import {ActivityAction, ActivityStatus} from "../../../../activities/activity";
import {GoogleAnalyticsService} from "../../../../shared/services/google-analytics/google-analytics.service";
import {Config} from "../../../../config/config";
import {UserSubscription} from "../../../../user/user-subscription";
import {TypeUtils} from "../../../../shared/utils/type-utils";
import {Utils} from "../../../../shared/utils/utils";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Injectable({
  providedIn: 'root'
})
export abstract class CreateAiBase implements OnInit, OnDestroy {
  @Input() public websiteId: number;
  @Input() isOnboarding = true;

  public recreationProcess = false;
  public showHigDemandNotice = false;
  public frontUrl: string;
  public demoUser: boolean;
  public fromStartAIPage: boolean;
  public regionsLoaded: boolean;
  public hideBackButton: boolean;

  protected user: User;
  protected userSubscription: UserSubscription;
  protected userLocalStorage: any = {};
  protected region: string;
  protected website: Website;

  protected _destroyStream$ = new Subject<boolean>();
  protected _destroyRef = inject(DestroyRef);

  protected constructor(
    protected readonly globalEventDispatcher: GlobalEventDispatcherService,
    protected readonly _store: Store<fromStore.DbStoreState>,
    protected readonly _hostingService: HostingService,
    protected readonly _userService: UserService,
    protected _googleAnalyticsService: GoogleAnalyticsService,
  ) {
  }

  ngOnInit() {
    this.fromStartAIPage = window.location.href.indexOf("start-ai-website-building") > -1;
    this.getUser();
    this.getCreatedWebsiteId();
    this._getWebsiteCreateOngoingActivity();
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  protected startRecreationProcess(): void {
    this.recreationProcess = true;
    this.globalEventDispatcher.dispatchEvent(GlobalEvent.hideOnboardingBackButton, 1);
    if (this.websiteId) {
      this.recreateWebsite();
    } else if (this.region) {
      this._createWebsite();
    } else {
      return this.errorHandler();
    }
  }

  /** create Website */
  private _createWebsite(): void {
    if (this.isOnboarding) {
      this._userService.setToLocal({...this.userLocalStorage, setupAiCreation: true});
    }
    const demoUser = (Utils.isDemoUser(this.userSubscription, this.user)) || this.fromStartAIPage;
    this._hostingService.createWebsiteForTrialUsers({region: this.region, siteTitle: 'My Website'}, demoUser)
      .pipe(
        first(response => !!response)
      )
      .subscribe(response => {
        if (!response.success) {
          return this.errorHandler();
        }
        this.websiteId = response.data && response.data.domain_id || null;

        this.createWebsiteObserver();
      });
  }

  protected createWebsiteObserver(): void {
    this._store.dispatch(new fromStore.UpdateUserSubscription());
    this._store.dispatch(new fromStore.LoadWebsites());
    this._store.dispatch(new fromStore.LoadWebsiteProducts(this.websiteId));
    this._getWebsite(this.websiteId)
      .then(() => this._getTenWebProductsAndActivate())
      .then(activitiesCount => this.getPluginActivities(activitiesCount))
      .then(() => this.recreateWebsite());
  }
  /** ---- */

  /** get Website */
  protected _getWebsite(websiteId): Promise<Website> {
    return new Promise<Website>((resolve) => {
      this._store.select(fromStore.getWebsitesLoaded)
        .pipe(
          first(loaded => loaded),
          switchMap(() => this._store.select(fromStore.getWebsite(websiteId))),
          first(website => !!website)
        )
        .subscribe(website => {
          this.website = website;
          this.getWebsiteObserver(website);
          resolve(null);
        });
    });
  }
  protected getWebsiteObserver(website: Website): void {
    this.frontUrl = website.site_url;
  }

  /** ---- */

  /** required plugins activation/installation */
  private _getTenWebProductsAndActivate(): Promise<number> {
    return new Promise<number>(resolve => {
      combineLatest(
        this._store.select(fromStore.getWebsitesLoaded),
        this._store.select(fromStore.getWebsitePluginsLoaded(this.websiteId)),
        this._store.select(fromStore.getWebsiteThemesLoaded(this.websiteId))
      )
        .pipe(
          first(([websitesLoaded, pluginsLoaded, themesLoaded]) => !!websitesLoaded && !!pluginsLoaded && !!themesLoaded),
          switchMap(() => combineLatest(
            this._store.select(fromStore.getWebsiteTenWebTheme(this.websiteId)),
            this._store.select(fromStore.getWebsiteElementorPlugin(this.websiteId)),
/*
            this._store.select(fromStore.getWebsiteBuilderPlugin(this.websiteId)),
*/
          )),
          first(),
        )
        .subscribe(products => {
          const productsToUpdate: Product[] = [];
          const productsToActivate: Product[] = [];
          products.forEach(product => {
            if (!product.active) {
              productsToActivate.push(product);
            }
            if (product.updateAvailable) {
              productsToUpdate.push(product);
            }
          });
          productsToUpdate.length && this.runProductAction(ActivityAction.UPDATE, productsToUpdate);
          productsToActivate.length && this.runProductAction(ActivityAction.ACTIVATE, productsToActivate);
          resolve(productsToUpdate.length + productsToActivate.length);
        });
    });
  }

  private runProductAction(action: ActivityAction, products: Product[]): void {
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products,
      action,
      network: 1
    }));
  }

  private getPluginActivities(remainingActivitiesCount: number): Promise<void> {
    return new Promise<void>(resolve => {
      if (!remainingActivitiesCount) return resolve();

      const pluginActivities$ = this._store.select(fromStore.getOngoingActivity)
        .pipe(
          filter(activity => activity && activity.websiteId === this.website.id
            && (activity.status === ActivityStatus.DONE || activity.status === ActivityStatus.FAILED)
            && activity.meta && activity.meta.product_data && activity.meta.product_data.type
            && (activity.meta.product_data.type === 'plugin' || activity.meta.product_data.type === 'theme')
          ),
          takeUntil(this._destroyStream$)
       )
        .subscribe(() => {
          remainingActivitiesCount--;
          if (!remainingActivitiesCount) {
            pluginActivities$.unsubscribe();
            resolve();
          }
        });
    });
  }
  /** ---- */

  /** recreate website */

  protected recreateWebsite(): void {
    if (this.isOnboarding) {
      this.userLocalStorage['setupAiCreation'] = false;
      this.userLocalStorage['setupAiRecreation'] = true;
      this._userService.setToLocal(this.userLocalStorage);
    }
    // API request
  }
  /** ---- */


  /** activities */
  private _getWebsiteCreateOngoingActivity(): void {
    this._store.select(fromStore.getOngoingActivity)
      .pipe(
        first(activity => !!activity && !!activity.websiteId && activity.action === ActivityAction.CREATE_WEBSITE),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(activity => {
        this.recreationProcess = true;
        this.globalEventDispatcher.dispatchEvent(GlobalEvent.hideOnboardingBackButton, 1);
        !this.websiteId && (this.websiteId = activity.websiteId);
      });
  }
  /** ---- */

  /** initial Data load */
  private getUser(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter(loaded => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserLocalStorage),
          this._store.select(fromStore.getUserSubscription),
        )),
        filter(([user, localStorage, userSubscription]) => !!user && !!localStorage && !!userSubscription),
        distinctUntilChanged(([prevUser], [currUser]) => prevUser && currUser && JSON.stringify(prevUser.signUpFor) === JSON.stringify(currUser.signUpFor)),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, local, userSubscription]) => {
        this.user = user;
        this.userLocalStorage = local;
        this.userSubscription = userSubscription;

        this.demoUser = Utils.isDemoUser(userSubscription, user) || this.fromStartAIPage;
        this.hideBackButton = (userSubscription && +userSubscription.product_id === Config.demoAiPluginId
          && [Config.defaultStarterPlanId, Config.basePlanId].includes(+userSubscription.subscriptionId));
        !TypeUtils.isNull(this.user) && !this.regionsLoaded && this.getRegion();
      });
  }

  private getRegion() {
    this._hostingService.regions(this.user.subscriptionId)
      .pipe(
        first(response => !!response)
      )
      .subscribe(response => {
        if (response.success) {
          this.regionsLoaded = true;
          const defaultRegion = response.data && response.data.find(region =>  region.is_available);
          this.region = defaultRegion && defaultRegion.zone || null;
        } else {
          this.errorHandler();
        }
      });
  }

  private getCreatedWebsiteId(): void {
    this._store.select(fromStore.getWebsitesLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => this._store.select(fromStore.getWebsites)),
        map(websites => websites && websites[0] && websites[0].id),
        first(websiteId => !!websiteId)
      )
      .subscribe(websiteId => {
          !this.websiteId && (this.websiteId = websiteId);
      });
  }
  /** ---- */

  /** Utility functions */
  protected errorHandler(): void {
    this.recreationProcess = false;
    NotifierComponent.notify(new Notification('Something Went Wrong...', NotificationType.DANGER));
  }
  /** ---- */
}
